import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { OpenInNew, AccessTime } from '@mui/icons-material';

import { useBaseCourtApi } from '../../../shared/hooks';
import { DEFAULT_QUERY_OPTIONS } from '../../../shared/hooks/useBaseCourtApi';
import { type CourtCaseCluster } from '../../../types';
import {
  formatCourtDateWithTime,
  formatClusterCitations,
} from '../../../utils';

import {
  HeaderCard,
  HeaderTopSection,
  TitleRow,
  TitleContainer,
  OpinionTitle,
  ExternalLinkIcon,
  LastUpdatedContainer,
  LastUpdatedIcon,
  LastUpdatedText,
  CourtNameLink,
  DetailsGrid,
  DetailPill,
  DetailPillContent,
  DetailIconContainer,
  DetailLabel,
  DetailValue,
} from './components';

interface OpinionDetailsHeaderProps {
  selectedCase: CourtCaseCluster;
}

export const OpinionDetailsHeader = ({
  selectedCase,
}: OpinionDetailsHeaderProps) => {
  const { links } = useAppConstantsContext();
  const { getDocket, getCourt } = useBaseCourtApi();

  const docketQuery = useQuery(
    ['docket', selectedCase.docket_id],
    () =>
      selectedCase.docket_id
        ? getDocket(selectedCase.docket_id.toString())
        : null,
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!selectedCase.docket_id,
    }
  );

  const courtQuery = useQuery(
    ['court', docketQuery.data?.court_id],
    () =>
      docketQuery.data?.court_id ? getCourt(docketQuery.data.court_id) : null,
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!docketQuery.data?.court_id,
    }
  );

  const docketInfo = docketQuery.data ?? null;
  const courtInfo = courtQuery.data ?? null;

  const getCourtDisplayText = () => {
    if (docketQuery.isLoading || courtQuery.isLoading) {
      return 'Loading...';
    }
    if (docketQuery.isError || courtQuery.isError) {
      return 'Error loading';
    }
    return courtInfo?.full_name || docketInfo?.court_id || 'Not Available';
  };

  const getDocketDisplayText = () => {
    if (docketQuery.isLoading) {
      return 'Loading...';
    }
    if (docketQuery.isError) {
      return 'Error loading';
    }
    return docketInfo?.docket_number || 'Not Available';
  };

  const handleCaseNameClick = () => {
    const legalSearchUrl = `/dashboard/court-cases/opinions?query=${encodeURIComponent(
      `caseName:${selectedCase.case_name}`
    )}`;
    window.open(
      `${links.baseUrl}${legalSearchUrl}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleDocketClick = () => {
    if (docketInfo?.docket_number) {
      const legalSearchUrl = `/dashboard/court-cases/opinions?query=${encodeURIComponent(
        `docketNumber:${docketInfo.docket_number}`
      )}`;
      window.open(
        `${links.baseUrl}${legalSearchUrl}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleCourtClick = () => {
    if (docketInfo?.court_id) {
      const courtName = courtInfo?.full_name || '';
      const legalSearchUrl = `/dashboard/court-cases/opinions?query=${encodeURIComponent(
        `court_id:${docketInfo.court_id}`
      )}${courtName ? `&courtName=${encodeURIComponent(courtName)}` : ''}`;
      window.open(
        `${links.baseUrl}${legalSearchUrl}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <HeaderCard>
      <HeaderTopSection>
        <TitleRow>
          <TitleContainer>
            <OpinionTitle>{selectedCase.case_name}</OpinionTitle>
            <ExternalLinkIcon onClick={handleCaseNameClick}>
              <OpenInNew />
            </ExternalLinkIcon>
          </TitleContainer>
          <LastUpdatedContainer>
            <LastUpdatedIcon>
              <AccessTime />
            </LastUpdatedIcon>
            <LastUpdatedText>
              Last Updated:{' '}
              {selectedCase.date_modified
                ? formatCourtDateWithTime(selectedCase.date_modified)
                : 'Not Available'}
            </LastUpdatedText>
          </LastUpdatedContainer>
        </TitleRow>
        <CourtNameLink
          onClick={docketInfo?.court_id ? handleCourtClick : undefined}
          sx={{
            cursor: docketInfo?.court_id ? 'pointer' : 'default',
            textDecoration: docketInfo?.court_id ? 'underline' : 'none',
          }}
        >
          {getCourtDisplayText()}
        </CourtNameLink>
      </HeaderTopSection>

      <DetailsGrid>
        {formatClusterCitations(selectedCase.citations) && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <AccessTime />
              </DetailIconContainer>
              <DetailLabel>Citations:</DetailLabel>
            </DetailPillContent>
            <DetailValue>
              {formatClusterCitations(selectedCase.citations)}
            </DetailValue>
          </DetailPill>
        )}

        {selectedCase.case_name_full && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <AccessTime />
              </DetailIconContainer>
              <DetailLabel>Full Case Name:</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedCase.case_name_full}</DetailValue>
          </DetailPill>
        )}

        <DetailPill>
          <DetailPillContent>
            <DetailIconContainer>
              <AccessTime />
            </DetailIconContainer>
            <DetailLabel>Docket:</DetailLabel>
          </DetailPillContent>
          <DetailValue
            onClick={docketInfo?.docket_number ? handleDocketClick : undefined}
            sx={{
              cursor: docketInfo?.docket_number ? 'pointer' : 'default',
              textDecoration: docketInfo?.docket_number ? 'underline' : 'none',
            }}
          >
            {getDocketDisplayText()}
          </DetailValue>
        </DetailPill>

        {selectedCase.date_filed && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <AccessTime />
              </DetailIconContainer>
              <DetailLabel>Date Filed:</DetailLabel>
            </DetailPillContent>
            <DetailValue>
              {new Date(selectedCase.date_filed).toLocaleDateString()}
            </DetailValue>
          </DetailPill>
        )}

        {selectedCase.precedential_status && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <AccessTime />
              </DetailIconContainer>
              <DetailLabel>Status:</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedCase.precedential_status}</DetailValue>
          </DetailPill>
        )}

        {selectedCase.judges && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <AccessTime />
              </DetailIconContainer>
              <DetailLabel>Judges:</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedCase.judges}</DetailValue>
          </DetailPill>
        )}
      </DetailsGrid>
    </HeaderCard>
  );
};
